import { takeLatest, call, put, select, all } from 'redux-saga/effects';

import {
  getGrants as getGrantsApi,
  getMemberTypes as getMemberTypesApi,
  getMembers as getMembersApi,
  createMember as createMemberApi,
  updateMember as updateMemberApi,
  deleteMember as deleteMemberApi,
  deleteMemberConnection as deleteMemberConnectionApi,
  getInvitations as getInvitationsApi,
  createInvitation as createInvitationApi,
  deleteInvitation as deleteInvitationApi,
  updateInvitation as updateInvitationApi,
  authorizeUser as authorizeUserApi,
  getServiceAccounts as getServiceAccountsApi,
  transferServiceAccountPermissions,
} from '../../services';
import { getUsers } from '../ad';
import { getSites } from '../sites';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import {
  REQUEST_GRANTS,
  REQUEST_MEMBER_TYPES,
  REQUEST_MEMBERS,
  CREATE_MEMBER_AND_CONNECTION,
  UPDATE_MEMBER_AND_CONNECTION,
  UPDATE_MEMBER,
  UPDATE_MEMBERS,
  DELETE_MEMBER,
  DELETE_MEMBER_CONNECTION,
  REQUEST_INVITATIONS,
  CREATE_INVITATIONS,
  UPDATE_INVITATION,
  UPDATE_INVITATIONS,
  DELETE_INVITATION,
  AUTHORIZE_USER,
  REQUEST_SERVICE_ACCOUNTS,
  TRANSFER_SERVICE_ACCOUNTS,
  requestMembers,
  receiveInvitations,
  receiveMembers,
  setCheckedInvitations,
  receiveGrants,
  receiveMemberTypes,
  receiveTenantUsers,
  requestInvitations,
  deleteInvitationSuccess,
  receiveServiceAccounts,
} from './actions';
import { getMembers } from './selectors';

export function* doRequestGrants({ oid, callback }) {
  try {
    const { permissions } = yield call(getGrantsApi, oid);
    yield put(receiveGrants(permissions));
  } catch (e) {
    yield console.error('Unable to fetch grants', e);
    yield call(checkOnline);
  }
  if (callback) yield callback();
}

export function* doRequestMemberTypes() {
  try {
    const memberTypes = yield call(getMemberTypesApi);
    yield put(receiveMemberTypes(memberTypes));
  } catch (e) {
    yield console.error('Unable to fetch member types: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getMemberTypes', 'error')()));
  }
}

export function* doRequestMembers(action) {
  try {
    const { objectId, indexUsers } = action;

    let members = [];

    if (objectId) {
      const { values } = yield call(getMembersApi, { objectId });
      members = values;
    } else {
      const { values } = yield call(getMembersApi);
      members = values;
    }

    if (indexUsers) {
      const sites = yield select(getSites);

      // build and return only users that are connected to site members, have an email,
      // are enabled, and include associated roles per site
      const indexedUsers = members.reduce((acc, member) => {
        member.connected.forEach((user) => {
          if (member.objectId?.indexOf('site/') === 0 && user.indexOf('users/') === 0) {
            const userId = user.split('/')[1];
            const siteId = member.objectId.split('/')[1];
            const validSite = sites.find((s) => s.id === siteId);
            if (acc[userId] && validSite) {
              acc[userId] = {
                roles: {
                  ...acc[userId].roles,
                  [member.type]: [...(acc[userId].roles[member.type] || []), siteId],
                },
              };
            } else if (validSite) {
              acc[userId] = {
                roles: {
                  [member.type]: [siteId],
                },
              };
            }
          }
        });
        return acc;
      }, {});

      const users = yield select(getUsers);
      const filteredUsers = {};
      Object.keys(indexedUsers).forEach((userId) => {
        const user = users.find((u) => u.id === userId);
        if (user && user.enabled && user.email) {
          filteredUsers[userId] = {
            ...indexedUsers[userId],
            username: user.username,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            name: user.name,
            emailVerified: user.emailVerified,
            createdAt: user.createdAt,
          };
        }
      });
      yield put(receiveTenantUsers(filteredUsers));
    }
    yield put(receiveMembers(members));
  } catch (e) {
    yield console.error('Unable to fetch members: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getMembers', 'error')()));
  }
}

export function* doUpdateOrCreateMemberConnection(action) {
  const { data, indexUsers, isUpdate } = action;
  const notifyType = isUpdate ? 'updateMember' : 'createMember';
  try {
    yield call(createMemberApi, data);
    yield put(requestMembers(undefined, indexUsers));
    yield put(displayNotification(getNotification(notifyType, 'success')(data.oid)));
  } catch (e) {
    yield console.error('Unable to add member: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification(notifyType, 'error')()));
  }
}

export function* doUpdateMember(action) {
  const {
    data: { id, ...memberUpdate },
  } = action;

  try {
    yield call(updateMemberApi, id, memberUpdate);
    yield put(displayNotification(getNotification('updateMember', 'success')()));
    yield put(requestMembers(undefined, true));
  } catch (e) {
    yield console.error('Unable to update member ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateMember', 'error')()));
  }
}

export function* doDeleteMember(action) {
  const { memberId } = action;

  try {
    yield call(deleteMemberApi, memberId);
    yield put(requestMembers(undefined, true));
    yield put(displayNotification(getNotification('deleteMember', 'success')(memberId)));
  } catch (e) {
    yield console.error('Unable to update member ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteMember', 'error')()));
  }
}

export function* doDeleteMemberConnection(action) {
  try {
    const { id, userId } = action;

    yield call(deleteMemberConnectionApi, id, userId);
    yield put(requestMembers(undefined, true));
    yield put(displayNotification(getNotification('deleteMemberConnection', 'success')()));
  } catch (e) {
    yield console.error('Unable to delete member: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteMemberConnection', 'error')()));
  }
}

export function* doUpdate(update) {
  yield call(createMemberApi, update);
}

export function* doRemove(data) {
  yield call(deleteMemberConnectionApi, data.id, data.userId);
}

export function* doUpdateUserMembers(action) {
  const {
    data: { roles: newRoles },
    user: { id: userId, roles: currentRoles },
  } = action;
  try {
    const members = yield select(getMembers);
    const sites = yield select(getSites);
    const updates = [];
    Object.entries(newRoles).forEach(([role, siteIds]) =>
      siteIds.forEach((siteId) => {
        const selectedSite = sites.find((s) => s.id === siteId);
        if (selectedSite) {
          const member = members.find(
            (m) => m.type === role && m.objectId === `org/${selectedSite.org}`
          );
          if (member && !(currentRoles[role] || []).includes(siteId)) {
            const postData = {
              scope: { org: selectedSite.org, site: siteId },
              permissions: member.permissions,
              type: member.type,
              objectId: `site/${siteId}`,
              oid: userId,
              templateId: member.id,
            };
            updates.push(postData);
          }
        }
      })
    );
    yield all(updates.map((update) => call(doUpdate, update)));

    const deletions = [];
    Object.entries(currentRoles).forEach(([role, siteIds]) =>
      siteIds.forEach((siteId) => {
        const member = members.find((m) => m.type === role && m.objectId === `site/${siteId}`);
        if (member && !newRoles[role].includes(siteId)) {
          deletions.push({ id: member.id, userId });
        }
      })
    );
    yield all(deletions.map((data) => call(doRemove, data)));

    yield put(requestMembers(undefined, true));
    yield put(displayNotification(getNotification('updateMember', 'success')(userId)));
  } catch (e) {
    yield console.error('Unable to update user: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateMember', 'error')()));
  }
}

export function* doRequestInvitations(action) {
  try {
    const { org } = action;
    const { values: invitations } = yield call(getInvitationsApi, { org });
    yield put(receiveInvitations(invitations));
  } catch (e) {
    yield console.error('Unable to fetch members: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getMembers', 'error')()));
  }
}

export function* doCreateInvitations(action) {
  const { data } = action;
  try {
    const { total } = yield call(createInvitationApi, data);
    yield put(displayNotification(getNotification('createInvitations', 'success')(total)));
    yield put(requestInvitations(data.org));
  } catch (e) {
    yield console.error('Unable to create invitation ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('createInvitations', 'error')()));
  }
}

export function* doUpdateInvitation(action) {
  const { data, invitationId } = action;
  try {
    yield call(updateInvitationApi, invitationId, data);
    yield put(displayNotification(getNotification('updateInvitation', 'success')(invitationId)));
    yield put(requestInvitations(data.org));
  } catch (e) {
    yield console.error('Unable to update invitation ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateInvitation', 'error')()));
  }
}
export function* doUpdateInvitations(action) {
  const { invitations } = action;
  const [
    {
      data: { org },
    },
  ] = invitations;
  try {
    yield all(invitations.map((invitation) => call(doUpdateInvitation, invitation)));
    yield put(displayNotification(getNotification('updateInvitations', 'success')()));
    yield put(requestInvitations(org));
  } catch (e) {
    yield console.error('Unable to update invitations ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateInvitations', 'error')()));
  }
}

export function* doDeleteInvitation(action) {
  try {
    const { invitationId } = action;

    yield call(deleteInvitationApi, invitationId);
    yield put(deleteInvitationSuccess(invitationId));
    yield put(displayNotification(getNotification('deleteInvitation', 'success')()));
  } catch (e) {
    yield console.error('Unable to delete member: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteInvitation', 'error')()));
  }
}

export function* doAuthorizeUser(action) {
  try {
    const { data } = action;

    yield call(authorizeUserApi, data);
    yield put(setCheckedInvitations(Date.now()));
  } catch (e) {
    yield console.warn('Unable to authorize user: ', e);
    yield call(checkOnline);
    yield put(setCheckedInvitations(Date.now()));
  }
}
export function* doRequestServiceAccounts() {
  try {
    const { values: serviceAccounts } = yield call(getServiceAccountsApi);
    yield put(receiveServiceAccounts(serviceAccounts));
  } catch (e) {
    yield console.error('Unable to fetch service accounts: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getServiceAccounts', 'error')()));
  }
}

export function* doTransferServiceAccount(action) {
  try {
    const { toId, fromId } = action;
    yield call(transferServiceAccountPermissions, { fromId, toId });
    yield call(doRequestServiceAccounts);
    yield put(displayNotification(getNotification('transferServiceAccounts', 'success')()));
  } catch (e) {
    yield console.error('Unable to transfer account: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('transferServiceAccounts', 'error')()));
  }
}

export const sagas = [
  takeLatest(REQUEST_GRANTS, doRequestGrants),
  takeLatest(REQUEST_MEMBER_TYPES, doRequestMemberTypes),
  takeLatest(REQUEST_MEMBERS, doRequestMembers),
  takeLatest(CREATE_MEMBER_AND_CONNECTION, doUpdateOrCreateMemberConnection),
  takeLatest(UPDATE_MEMBER_AND_CONNECTION, doUpdateOrCreateMemberConnection),
  takeLatest(UPDATE_MEMBER, doUpdateMember),
  takeLatest(UPDATE_MEMBERS, doUpdateUserMembers),
  takeLatest(DELETE_MEMBER, doDeleteMember),
  takeLatest(DELETE_MEMBER_CONNECTION, doDeleteMemberConnection),
  takeLatest(REQUEST_INVITATIONS, doRequestInvitations),
  takeLatest(CREATE_INVITATIONS, doCreateInvitations),
  takeLatest(UPDATE_INVITATION, doUpdateInvitation),
  takeLatest(UPDATE_INVITATIONS, doUpdateInvitations),
  takeLatest(DELETE_INVITATION, doDeleteInvitation),
  takeLatest(AUTHORIZE_USER, doAuthorizeUser),
  takeLatest(REQUEST_SERVICE_ACCOUNTS, doRequestServiceAccounts),
  takeLatest(TRANSFER_SERVICE_ACCOUNTS, doTransferServiceAccount),
];
